import {
    Component,
    computed,
    DestroyRef,
    type ElementRef,
    effect,
    inject,
    signal,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import type { MenuItem } from '@openng/optimus-ui/api';
import { ButtonModule } from '@openng/optimus-ui/button';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { MenuModule } from '@openng/optimus-ui/menu';
import { MessageModule } from '@openng/optimus-ui/message';
import { ProgressSpinnerModule } from '@openng/optimus-ui/progressspinner';
import { TooltipModule } from '@openng/optimus-ui/tooltip';

import { AppConfigService } from '@app/shared/api/app-config.service';
import { ClipboardService } from '@app/shared/browser/clipboard.service';
import { ToastNotificationService } from '@app/shared/notification/toast-notification.service';
import {
    DatabaseMigrationService,
    type DBUpgradeLogEvent,
} from '@app/theme/database-migration-modal/database-migration.service';

enum UPGRADE_STATUS {
    PENDING = 'pending',
    IN_PROGRESS = 'in_progress',
    SUCCESS = 'success',
    ERROR = 'error',
}

@Component({
    selector: 'cor-database-migration-modal',
    templateUrl: './database-migration-modal.component.html',
    styleUrls: ['./database-migration-modal.component.scss'],
    imports: [
        DialogModule,
        ButtonModule,
        FormsModule,
        InputTextModule,
        ProgressSpinnerModule,
        MessageModule,
        MenuModule,
        TooltipModule,
    ],
})
export class DatabaseMigrationModalComponent {
    readonly logsContainer = viewChild<ElementRef<HTMLDivElement>>('logsContainer');

    protected readonly UPGRADE_STATUS = UPGRADE_STATUS;

    protected readonly migrationService = inject(DatabaseMigrationService);
    private readonly clipboardService = inject(ClipboardService);
    private readonly toast = inject(ToastNotificationService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly appConfigService = inject(AppConfigService);
    private readonly route = inject(ActivatedRoute);

    readonly logs = signal<string[]>([]);
    protected readonly showUpgradeButton = signal(false);
    // When the server has an ADMIN_KEY, the upgrade only starts with the key the administrator knows
    protected readonly secureAdmin = computed(
        () => !!this.appConfigService.appConfig().secureAdmin,
    );
    protected readonly adminKey = signal('');
    readonly upgradeStatus = signal<UPGRADE_STATUS>(UPGRADE_STATUS.PENDING);
    readonly upgradeError = signal<string | null>(null);

    constructor() {
        // Auto-scroll when logs update
        effect(() => {
            this.logs();
            if (this.logsContainer()) {
                this.logsContainer().nativeElement.scrollTo({
                    top: this.logsContainer().nativeElement.scrollHeight,
                    behavior: 'smooth',
                });
            }
        });

        // ?admin=1 (or true) opens the upgrade straight away, without the "I'm the administrator" click
        this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
            const adminFlag = params.get('admin')?.toString().toLowerCase();
            if (adminFlag === 'true' || adminFlag === '1') {
                this.showUpgradeButton.set(true);
            }
        });
    }

    // Right click on the start button: the first item is what a click does
    protected readonly startMenuItems: MenuItem[] = [
        {
            label: 'Migrate after creating a DB backup (default)',
            icon: 'pi pi-check',
            command: () => this.startUpgrade(),
        },
        {
            label: 'Migrate without a DB backup (dangerous)',
            icon: 'pi pi-exclamation-triangle',
            styleClass: 'database-migration-no-backup',
            command: () => this.startUpgrade(false),
        },
    ];

    startUpgrade(backup = true): void {
        this.upgradeStatus.set(UPGRADE_STATUS.IN_PROGRESS);
        this.logs.set([]);

        this.migrationService
            .dbUpgrade(this.secureAdmin() ? this.adminKey() : undefined, backup)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (event: DBUpgradeLogEvent) => {
                    if (event.type === 'log') {
                        if (event.message) {
                            this.logs.update(logs => [...logs, event.message]);
                        }
                    } else if (event.type === 'completed') {
                        if (event.success) {
                            this.upgradeStatus.set(UPGRADE_STATUS.SUCCESS);
                            this.toast.showSuccess('Database upgrade completed successfully!');
                        } else {
                            this.upgradeStatus.set(UPGRADE_STATUS.ERROR);
                            this.upgradeError.set(event.error || 'Unknown error occurred');
                            this.logs.update(logs => [
                                ...logs,
                                '',
                                '-----------------------------------',
                                'Database upgrade failed:',
                                this.upgradeError(),
                            ]);
                            this.toast.showError(`Database upgrade failed: ${this.upgradeError()}`);
                        }
                    }
                },
                error: (error: Event) => {
                    console.error('Websocket error:', error);
                    let message = 'Error connecting to the DB.';
                    if (error instanceof CloseEvent && error.reason) {
                        message += ` Reason: ${error.reason}`;
                    } else if (error.type) {
                        message += ` Reason: ${error.type}`;
                    }
                    this.toast.showError(message);
                    this.upgradeStatus.set(UPGRADE_STATUS.ERROR);
                },
            });
    }

    onClose(): void {
        if (this.upgradeStatus() !== UPGRADE_STATUS.IN_PROGRESS) {
            this.migrationService.visible.set(false);
        }
    }

    continueToPlatform(): void {
        this.migrationService.visible.set(false);
        window.location.reload();
    }

    copyLogs(): void {
        const logsText = this.logs().join('\n');
        if (!logsText.trim()) {
            this.toast.showInfo('No logs to copy.');
            return;
        }

        this.clipboardService.copyToClipboard(logsText).then(
            () => this.toast.showSuccess('Logs copied to clipboard.'),
            () => this.toast.showError('Failed to copy logs to clipboard.'),
        );
    }
}
